import React, { useState } from 'react';

import { Link as RouterLink } from '~/lib/react-router-dom';

import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';

import {
  LUXPLUS_PROMO_BANNER_CAROUSEL_FLAG,
  LUXPLUS_PROMO_CARD_CAROUSEL_FLAG,
} from '~/utils/luxPlusPayloadBuilder/constants';

import { PAYLOAD_BUILDER_ALERT_BUTTON_SX } from '../PayloadBuilderConstants';

import type { LuxPlusPayloadBuilderMode } from './LuxPlusPayloadBuilderToolbar';

interface LuxPlusPayloadBuilderSetupGuideProps {
  selectedMode: LuxPlusPayloadBuilderMode;
}

const BUTTON_LABEL: Record<LuxPlusPayloadBuilderMode, string> = {
  promoCardCarousel: 'How to Customise the Cards',
  promoBannerCarousel: 'How to Customise the Banners',
};

const FLAG_BY_MODE: Record<LuxPlusPayloadBuilderMode, string> = {
  promoCardCarousel: LUXPLUS_PROMO_CARD_CAROUSEL_FLAG,
  promoBannerCarousel: LUXPLUS_PROMO_BANNER_CAROUSEL_FLAG,
};

function Step({ number, title, children }: { number: number; title: string; children: React.ReactNode }) {
  return (
    <ListItem alignItems="flex-start" sx={{ px: 0, py: 1.5 }}>
      <Box
        sx={{
          minWidth: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          fontWeight: 700,
          mr: 1.5,
          mt: 0.25,
          flexShrink: 0,
        }}
      >
        {number}
      </Box>
      <ListItemText
        primary={
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            {title}
          </Typography>
        }
        secondary={
          <Typography variant="body2" component="div" color="text.secondary">
            {children}
          </Typography>
        }
      />
    </ListItem>
  );
}

function SetupGuideContent({ selectedMode }: { selectedMode: LuxPlusPayloadBuilderMode }) {
  const flagName = FLAG_BY_MODE[selectedMode];
  const itemLabel = selectedMode === 'promoCardCarousel' ? 'promo cards' : 'promo banners';
  const title =
    selectedMode === 'promoCardCarousel' ? 'How to customise the promo cards' : 'How to customise the promo banners';

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This page builds the JSON payload for the LuxPlus+ {itemLabel} Lab remote flag. Start from the existing Lab
          flag payload, make your changes here, then paste the validated JSON back into the same flag.
        </Typography>

        <List disablePadding>
          <Step number={1} title="Copy the current Lab payload">
            <>
              Open{' '}
              <Link component={RouterLink} to="/lab" target="_blank" rel="noopener noreferrer">
                Lab
              </Link>
              , go to <strong>Feature flags → Remote Flag</strong>, find <code>{flagName}</code>, and copy the existing
              payload.
            </>
          </Step>

          <Step number={2} title="Load it into the payload builder">
            <>
              Select <strong>{selectedMode === 'promoCardCarousel' ? 'Promo Cards' : 'Promo Banners'}</strong>, paste
              the JSON into <strong>Load existing payload</strong>, then load it so the form and live preview are
              populated from the current flag.
            </>
          </Step>

          <Step number={3} title={`Customise the ${itemLabel}`}>
            <>
              Add, remove, or rearrange items. Update copy, regions, optional expiry, and type-specific fields, then
              make sure the live preview shows no validation errors. Dates are configured in Australia/Sydney and
              exported as UTC.
            </>
          </Step>

          <Step number={4} title="Paste the updated payload back into Lab">
            <>
              Use <strong>Copy JSON</strong> from the toolbar, go back to <strong>Feature flags → Remote Flag</strong>,
              open <code>{flagName}</code>, paste the JSON into the payload section, and save.
            </>
          </Step>
        </List>
      </DialogContent>
    </>
  );
}

export default function LuxPlusPayloadBuilderSetupGuide({ selectedMode }: LuxPlusPayloadBuilderSetupGuideProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="info"
        startIcon={<InfoOutlinedIcon fontSize="small" />}
        onClick={() => setIsOpen(true)}
        sx={PAYLOAD_BUILDER_ALERT_BUTTON_SX}
      >
        {BUTTON_LABEL[selectedMode]}
      </Button>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth="sm" fullWidth>
        <SetupGuideContent selectedMode={selectedMode} />
        <DialogActions>
          <Button variant="contained" onClick={() => setIsOpen(false)}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
